import { useEffect, useState } from 'react';
import Card from './Card';
import gameApiService from '../services/gameApiService';

interface RabbitCard {
  rank: string;
  suit: string;
}

interface RabbitHuntModalProps {
  open: boolean;
  onClose: () => void;
  tableId: string;
  handNumber: number;
}

const SUIT_NAME: Record<string, 'hearts' | 'diamonds' | 'clubs' | 'spades'> = {
  hearts: 'hearts',
  diamonds: 'diamonds',
  clubs: 'clubs',
  spades: 'spades',
  h: 'hearts',
  d: 'diamonds',
  c: 'clubs',
  s: 'spades',
  '': 'hearts',

  '': 'diamonds',

  '': 'clubs',

  '': 'spades',

};

const normalizeSuit = (s: string) => SUIT_NAME[s?.toLowerCase?.() ?? s] ?? 'spades';
const normalizeRank = (r: string) => (r === 'T' ? '10' : r) as any;

function RabbitHuntModal({ open, onClose, tableId, handNumber }: RabbitHuntModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [board, setBoard] = useState<RabbitCard[] | null>(null);
  const [existingCount, setExistingCount] = useState(0);
  const [revealedCount, setRevealedCount] = useState(0);

  useEffect(() => {
    if (!open) {
      setBoard(null);
      setError(null);
      setRevealedCount(0);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    gameApiService.dealRabbitHunt(tableId, handNumber).then(result => {
      if (cancelled) return;
      setLoading(false);
      if (!result.success || !result.fullBoard) {
        setError(result.error || 'Could not retrieve runout');
        return;
      }
      setBoard(result.fullBoard);
      setExistingCount(result.existingCommunityCards?.length || 0);
      setRevealedCount(result.existingCommunityCards?.length || 0);
    });
    return () => { cancelled = true; };
  }, [open, tableId, handNumber]);

  // Reveal rabbit cards one-by-one
  useEffect(() => {
    if (!board) return;
    if (revealedCount >= board.length) return;
    const t = setTimeout(() => setRevealedCount(c => c + 1), 450);
    return () => clearTimeout(t);
  }, [board, revealedCount]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1a3a5c 0%, #0d1f33 100%)',
          border: '2px solid #d4af37',
          borderRadius: 16,
          padding: 24,
          maxWidth: 720,
          width: '100%',
          color: '#fff',
          boxShadow: '0 20px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 22, color: '#d4af37' }}> Rabbit Hunt</h2>

          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', color: '#fff',
              fontSize: 24, cursor: 'pointer', padding: 4,
            }}
            aria-label="Close"
          >×</button>
        </div>

        <p style={{ margin: '0 0 20px 0', fontSize: 14, opacity: 0.85 }}>
          Here's what the runout would have been:
        </p>

        {loading && (
          <div style={{ textAlign: 'center', padding: 32, opacity: 0.7 }}>Dealing the rabbit…</div>
        )}

        {error && (
          <div style={{
            background: 'rgba(231, 76, 60, 0.15)',
            border: '1px solid rgba(231, 76, 60, 0.4)',
            borderRadius: 8, padding: 16, fontSize: 14, color: '#ffb3a8',
          }}>
            {error}
          </div>
        )}

        {board && (
          <div style={{
            display: 'flex', gap: 8, justifyContent: 'center',
            flexWrap: 'wrap', padding: '12px 0',
          }}>
            {board.map((card, i) => {
              const isExisting = i < existingCount;
              const isRevealed = i < revealedCount;
              return (
                <div
                  key={`rabbit-${i}`}
                  style={{
                    transform: isRevealed ? 'rotateY(0deg) scale(1)' : 'rotateY(180deg) scale(0.9)',
                    transition: 'transform 400ms ease',
                    opacity: isRevealed ? 1 : 0.3,
                    filter: isExisting ? 'none' : 'drop-shadow(0 0 12px rgba(212, 175, 55, 0.6))',
                  }}
                >
                  {isRevealed ? (
                    <Card
                      suit={normalizeSuit(card.suit)}
                      rank={normalizeRank(card.rank)}
                    />
                  ) : (
                    <div style={{
                      width: 80, height: 120, borderRadius: 8,
                      background: 'linear-gradient(135deg, #8B0000, #5a0000)',
                      border: '2px solid #d4af37',
                    }} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {board && revealedCount >= board.length && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 13, opacity: 0.7 }}>
            {existingCount === 0 ? 'Folded preflop' :
              existingCount === 3 ? 'Folded on flop' :
              existingCount === 4 ? 'Folded on turn' : ''}
          </div>
        )}
      </div>
    </div>
  );
}

export default RabbitHuntModal;
